import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ImageDown, ArrowRight } from "lucide-react";
import { getShare, sharePhotoUrl } from "@/lib/api";
import { PhotoGrid } from "@/components/JobPhotos";
import { scalarEntries, renderCard } from "@/lib/share";

function Rows({ title, entries, emphasisFirst, testid }) {
  if (!entries.length) return null;
  return (
    <div data-testid={testid} className="border border-[#334155] bg-[#11151c]">
      <div className="px-5 py-3 border-b border-[#334155] mono text-[10px] tracking-widest text-[#f59e0b]">{title}</div>
      <div className="px-5 py-2">
        {entries.map((e, i) => (
          <div key={e.key} className="flex items-baseline justify-between py-2 border-b border-[#334155]/50 last:border-0">
            <span className="text-xs mono uppercase tracking-wider text-[#94a3b8]">{e.label}</span>
            <span className={`mono ${emphasisFirst && i === 0 ? "text-xl font-bold text-[#fbbf24]" : "text-[#f8fafc]"}`}>
              {e.value} {e.unit && <span className="text-[10px] text-[#64748b]">{e.unit}</span>}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function SharedResult() {
  const { id } = useParams();
  const [share, setShare] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    getShare(id).then(setShare).catch(() => setError(true));
  }, [id]);

  const download = async () => {
    const blob = await renderCard(share);
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `rigcalc-${share.calculator}.png`;
    a.click();
  };

  return (
    <div data-testid="shared-result-page" className="min-h-screen bg-[#0b0e13] text-[#f8fafc]">
      <div className="h-1 bg-[#f59e0b]" />
      <header className="max-w-3xl mx-auto px-5 py-6 flex items-center justify-between">
        <Link to="/" data-testid="shared-brand-link" className="flex items-center gap-3">
          <img src="/icon-192.png" alt="RIGCALC" className="w-10 h-10" />
          <span className="heading text-lg font-bold">RIGCALC</span>
        </Link>
        <Link to="/tools" data-testid="shared-try-top" className="mono text-[10px] tracking-widest uppercase border border-[#334155] hover:border-[#f59e0b] hover:text-[#f59e0b] px-3 py-2 transition-colors">
          Try RIGCALC
        </Link>
      </header>
      <main className="max-w-3xl mx-auto px-5 pb-16">
        {error && <div data-testid="shared-not-found" className="border border-[#334155] p-8 text-center text-[#94a3b8]">This shared result was not found or has been removed.</div>}
        {!share && !error && <div className="mono text-xs text-[#64748b]">Loading…</div>}
        {share && (
          <>
            <div className="mono text-[10px] tracking-[0.3em] text-[#f59e0b] mb-2">{share.category?.toUpperCase()}</div>
            <h1 data-testid="shared-title" className="heading text-4xl sm:text-5xl font-bold mb-2">{share.title}</h1>
            <p className="text-xs mono text-[#64748b] mb-8">
              Shared by {share.sharer_name || "a RIGCALC user"} · {new Date(share.created_at).toLocaleString()} · {share.units === "metric" ? "Metric" : "Field"} units
            </p>
            <div className="grid gap-5 md:grid-cols-2 mb-6">
              <Rows title="RESULT" entries={scalarEntries(share.outputs)} emphasisFirst testid="shared-outputs" />
              <Rows title="INPUTS" entries={scalarEntries(share.inputs)} testid="shared-inputs" />
            </div>
            <button data-testid="shared-download-image" onClick={download} className="inline-flex items-center gap-2 border border-[#334155] hover:border-[#f59e0b] hover:text-[#f59e0b] px-4 py-2 mono text-xs uppercase tracking-widest transition-colors mb-10">
              <ImageDown className="w-4 h-4" /> Download image
            </button>
            {share.photo_ids?.length > 0 && (
              <section className="mb-10" data-testid="shared-photos">
                <div className="mono text-[10px] tracking-widest text-[#f59e0b] mb-3">WELL SITE PHOTOS</div>
                <PhotoGrid photos={share.photo_ids.map((pid) => ({ id: pid }))} srcFor={(p) => sharePhotoUrl(share.id, p.id)} testid="shared-photos-grid" />
              </section>
            )}
            <div className="border border-[#f59e0b]/40 bg-[#f59e0b]/5 p-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div>
                <div className="heading font-bold">Run your own numbers</div>
                <div className="text-sm text-[#94a3b8]">30+ oilfield calculators built for rig crews. Works offline.</div>
              </div>
              <Link to="/pricing" data-testid="shared-try-cta" className="inline-flex items-center gap-2 bg-[#f59e0b] hover:bg-[#fbbf24] text-[#0b0e13] px-5 py-3 mono text-xs tracking-widest uppercase font-bold transition-colors">
                Try RIGCALC <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <p className="text-[11px] text-[#64748b] mt-6">Reference only. Verify against your company procedures before acting on any result.</p>
          </>
        )}
      </main>
    </div>
  );
}
